/* eslint-disable react/prop-types */
import { Tag } from "@egaranti/components";

import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import InfoTooltip from "../shared/infoTooltip";
import GeneralSummaryCard from "./generalSummaryCard";
import GeneralSummarySectionSkeleton from "./generalSummarySectionSkeleton";

import { animate } from "framer-motion";
import { Building, Package, PackageCheck, Users } from "lucide-react";

const GeneralSummarySection = ({ data, loading }) => {
  const { t } = useTranslation();

  if (loading || !data) return <GeneralSummarySectionSkeleton />;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col justify-between gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-[#111729]">
            {t("overviewPage.generalSummary")}
          </h2>
          <span className="flex items-center gap-1 rounded-lg border border-[#EAECF0] bg-[#F2F5F9] px-5 py-2 text-sm font-medium text-[#111729]">
            <span
              dangerouslySetInnerHTML={{
                __html: t("overviewPage.creditsMessage", {
                  count: data.credits,
                }),
              }}
            />
            <InfoTooltip>{t("overviewPage.infoCredit")}</InfoTooltip>
          </span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {/* Card 1: Product Count */}
          <GeneralSummaryCard>
            <GeneralSummaryCard.Content icon={Package}>
              <h2 className="text-sm font-semibold text-[#677389]">
                {t("overviewPage.product")}
              </h2>
              <h3 className="text-xl font-semibold text-[#111729]">
                <Counter
                  from={0}
                  to={data.merchantProductsCount.merchantProductCount}
                />
              </h3>
            </GeneralSummaryCard.Content>
          </GeneralSummaryCard>

          {/* Card 2: Integration Product Count */}
          <GeneralSummaryCard>
            <GeneralSummaryCard.Content icon={PackageCheck}>
              <h2 className="text-sm font-semibold text-[#677389]">
                {t("overviewPage.productfromintegration")}
              </h2>
              <h3 className="text-xl font-semibold text-[#111729]">
                <Counter
                  from={0}
                  to={
                    data.merchantProductsCount.merchantIntegrationProductCount
                  }
                />
              </h3>
            </GeneralSummaryCard.Content>
          </GeneralSummaryCard>

          {/* Card 3: Individual Customer Count */}
          <GeneralSummaryCard>
            <GeneralSummaryCard.Content icon={Users}>
              <h2 className="text-sm font-semibold text-[#677389]">
                {t("overviewPage.individualCustomer")}
              </h2>
              <h3 className="text-xl font-semibold text-[#111729]">
                <Counter
                  from={0}
                  to={
                    data.customersCount.individualCustomerStatDto
                      .individualCustomerCount
                  }
                />
              </h3>
            </GeneralSummaryCard.Content>
          </GeneralSummaryCard>

          {/* Card 4: Corporate Customer Count */}
          <GeneralSummaryCard>
            <GeneralSummaryCard.Content icon={Building}>
              <h2 className="text-sm font-semibold text-[#677389]">
                {t("overviewPage.corporateCustomer")}
              </h2>
              <h3 className="text-xl font-semibold text-[#111729]">
                <Counter
                  from={0}
                  to={
                    data.customersCount.corporateCustomerStatDto
                      .corporateCustomerCount
                  }
                />
              </h3>
            </GeneralSummaryCard.Content>
          </GeneralSummaryCard>
        </div>
      </div>
    </section>
  );
};

export default GeneralSummarySection;

function Counter({ from, to }) {
  const nodeRef = useRef();

  useEffect(() => {
    const node = nodeRef.current;

    const controls = animate(from, to, {
      duration: 1.5,
      ease: "easeOut",
      onUpdate(value) {
        node.textContent = Math.round(value).toLocaleString();
      },
    });

    return () => controls.stop();
  }, [from, to]);

  return <p ref={nodeRef} className="py-0" />;
}
